/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'underscore',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/dashboard-common/dist/api/impl/CallbackInvoker',
	'../../../lib/@waca/dashboard-common/dist/utils/ActionTypes',
	'../../../lib/@waca/dashboard-common/dist/api/ContentActionsProviderAPI',
	'../../../api/DashboardAPI',
	'./ToolbarDockAPI'
], function(_, APIFactory, CallbackInvoker, ActionTypes, ContentActionsProviderAPI, DashboardAPI, ToolbarDockAPI){
	class ToolbarDock extends CallbackInvoker {

		constructor({ features }) {
			const dashboard = features.API;
			const logger = dashboard.getService(DashboardAPI.GLOBAL_SERVICES.LOGGER);
			super({ logger });
			this._features  = features;

			this._dashboard = dashboard;

			this._dockedToolbarState = {
				actions: [],
				selectedIds: []
			};

			this._api = APIFactory.createAPI(this, [ToolbarDockAPI, CallbackInvoker]);

			this._dashboard.getCanvasWhenReady().then((canvas) => {
				this._canvas = canvas;
				canvas.on('change:selections:select', this.onContentSelectionChange, this);
				canvas.on('change:selections:deselect', this.onContentSelectionChange, this);
				this._dashboard.on('dashboard:show', this.onDashboardShow, this);
				this._currentUIState = Object.assign({}, features.DashboardState.getUiState());
				features.DashboardState.onUiStateChange(this._uiStateChange.bind(this));
				this._updateToolbars();
			});
		}

		getAPI() {
			return this._api;
		}

		destroy() {
			if (this._canvas) {
				this._canvas.off('change:selections:select', this.onContentSelectionChange, this);
				this._canvas.off('change:selections:deselect', this.onContentSelectionChange, this);
			}
			if (this._dashboard) {
				this._dashboard.off('dashboard:show', this.onDashboardShow, this);
			}
			this._api = null;
		}

		getOwnerIDs() {
			return this._dockedToolbarState.selectedIds;
		}

		onDashboardShow() {
			this._updateToolbars();
		}

		onContentSelectionChange() {
			this._updateToolbars(false, false, true);
		}

		_getSelectedIds() {
			let selectedIds = [];
			if (this._currentUIState.focus) {
				const widgets = this._canvas.getWidgets();
				for (let i = 0; i < widgets.length; i++) {
					if (widgets[i].isWidgetMaximized()) {
						selectedIds.push(widgets[i].getId());
						break;
					}
				}
			} else {
				const currentSelection = this._canvas.getSelectedContentList();
				selectedIds = currentSelection.map(content => content.getId());
				selectedIds.sort();
			}
			return selectedIds;
		}

		_updateToolbars(forceRenderLeading = true, shouldRenderTrailing = true, shouldFocusLeadingSection = true) {
			const selectedIds = this._getSelectedIds();
			const actions = this._features['ContentActions'].getContentActionList(selectedIds);
			if (forceRenderLeading || !_.isEqual(this._dockedToolbarState.selectedIds, selectedIds)) {
				this._dockedToolbarState.selectedIds = selectedIds;
				this._updateLeadingToolbar(actions);
				if (shouldFocusLeadingSection && selectedIds.length) {
					this.focusODT();
				}
			}
			if (shouldRenderTrailing) {
				this._updateTrailingToolbar(actions);
			}
		}

		_updateLeadingToolbar(actions) {
			const leadingActions = actions.filter(
				(action) => action.section !== ContentActionsProviderAPI.ACTION_SECTION.PANEL
			);
			this._dockedToolbarState.actions = this._orderActions(leadingActions);
			this.invokeCallbacks(ToolbarDockAPI.UPDATE_DOCKED_TOOLBAR_STATE, this._dockedToolbarState);
		}

		_updateTrailingToolbar(actions) {
			let trailingActions = actions.filter(
				(action) => action.section === ContentActionsProviderAPI.ACTION_SECTION.PANEL
			);
			trailingActions = this._orderActions(trailingActions);
			this.invokeCallbacks(ToolbarDockAPI.UPDATE_CANVAS_TOOLBAR_STATE, { actions: trailingActions });
		}

		/** API implementations */

		/**
		 * @implements ToolbarDockAPI#updateODTState
		 */
		updateODTState(actions, selectedIds = undefined) {
			this._dockedToolbarState.selectedIds = selectedIds;
			this._updateLeadingToolbar(actions);
		}

		_uiStateChange(updatedState) {
			const previousState = this._currentUIState;
			this._currentUIState = Object.assign({}, updatedState);
			const statePropertiesMonitored = ['focus', 'fullScreen', 'dirty', 'authoring', 'eventGroups'];
			const updateLeading = statePropertiesMonitored.some(
				stateProperty => updatedState[stateProperty] !== previousState[stateProperty]
			);
			this._updateToolbars(updateLeading, true, false);
		}

		_compareByKey(a, b, key) {
			const compareA = typeof a[key] === 'undefined' ?  Number.MAX_SAFE_INTEGER : a[key];
			const compareB = typeof b[key] === 'undefined' ?  Number.MAX_SAFE_INTEGER : b[key];
			return compareA - compareB;
		}

		_orderActions(actions) {
			actions.sort((a, b) => {
				const comparisonResult = this._compareByKey(a, b, 'section');
				if (comparisonResult) {
					return comparisonResult;
				}
				return this._compareByKey(a, b, 'order');
			});

			return actions;
		}

		/*
		 * @implements ToolbarDockAPI#focusODT
		 */
		focusODT() {
			this.invokeCallbacks(ToolbarDockAPI.FOCUS_DOCKED_TOOLBAR);
		}

	}
	return ToolbarDock;
});
